import { TApiServiceOptions } from "../apiService";
import { API } from "../../core/apiurl";
import { authenticatedApiService } from "../apiservice.auth";
import {
  TEmployeeListResponse,
  TEmployeeDetailResponse,
  TCreateEmployeeForm,
  TEmployeeCreationResponse,
  TEmployeeUpdateResponse,
  TBulkImportResponse,
  TBulkImportPreviewResponse,
  TConvertToUserRequest,
  TConvertToUserResponse,
  TEmployeeInvitationListResponse,
  TEmployeeInvitationDetailResponse,
  TCreateInvitationForm,
  TResendInvitationForm,
  TValidateInvitationTokenResponse,
  TAcceptInvitationForm,
  TInvitationStatsResponse,
  TTenantSettingsResponse,
  TUpdateTenantSettingsForm,
  TCreateUpdateEmployeePayload,
  TEmployeeColumnsResponse,
  TEmployeeChoicesResponse,
  TBulkEmployeePermissionAssignmentRequest,
  TBulkEmployeePermissionAssignmentResponse,
} from "./employee-management.types";
import {
  TApiResponseWrapper,
  TAssignedPermissionListResponse,
} from "../role-management/role-management.types";
import { TEmployeeAttributeHistoryResponse } from "../employee-attributes/employee-attributes.types";
class EmployeeManagementService {
  // Employee CRUD operations
  getEmployeeList(query: string, options?: TApiServiceOptions) {
    return authenticatedApiService.get<TEmployeeListResponse>(
      API.employees + query,
      options,
    );
  }

  getEmployeeChoices(options?: TApiServiceOptions) {
    return authenticatedApiService.get<TEmployeeChoicesResponse>(
      API.employee_choices,
      options,
    );
  }

  getEmployeeColumns(query: string, options?: TApiServiceOptions) {
    return authenticatedApiService.get<TEmployeeColumnsResponse>(
      `${API.employees}columns/?${query}`,
      options,
    );
  }

  getEmployeeById(internalId: string, options?: TApiServiceOptions) {
    return authenticatedApiService.get<TEmployeeDetailResponse>(
      `${API.employees}${internalId}/`,
      options,
    );
  }

  createEmployee(data: TCreateEmployeeForm, options?: TApiServiceOptions) {
    return authenticatedApiService.post<TEmployeeCreationResponse>(
      API.employees,
      data,
      options,
    );
  }

  updateEmployee(
    data: TCreateUpdateEmployeePayload,
    options?: TApiServiceOptions,
  ) {
    return authenticatedApiService.put<TEmployeeUpdateResponse>(
      `${API.employees}${data?.internal_id}/`,
      data?.data,
      options,
    );
  }

  deleteEmployee(internalId: string, options?: TApiServiceOptions) {
    return authenticatedApiService.delete(
      `${API.employees}${internalId}/`,
      options,
    );
  }

  // Bulk upload operations
  uploadEmployeeFile(formData: FormData, options?: TApiServiceOptions) {
    return authenticatedApiService.post<TBulkImportResponse>(
      API.employees_bulk_upload,
      formData,
      {
        ...options,
        headers: { ...options?.headers },
      },
    );
  }

  previewBulkImport(data: FormData, options?: TApiServiceOptions) {
    return authenticatedApiService.post<TBulkImportPreviewResponse>(
      API.preview_bulk_import,
      data,
      options,
    );
  }

  // User conversion operations
  convertEmployeeToUser(
    employeeId: string,
    data: TConvertToUserRequest,
    options?: TApiServiceOptions,
  ) {
    return authenticatedApiService.post<TConvertToUserResponse>(
      `${API.convert_employee_to_user}${employeeId}/convert_to_user/`,
      data,
      options,
    );
  }

  // Employee invitation operations
  getEmployeeInvitationList(query: string, options?: TApiServiceOptions) {
    return authenticatedApiService.get<TEmployeeInvitationListResponse>(
      API.employee_invitation_list + query,
      options,
    );
  }

  getEmployeeInvitationById(internalId: string, options?: TApiServiceOptions) {
    return authenticatedApiService.get<TEmployeeInvitationDetailResponse>(
      `${API.employee_invitation_detail}?internal_id=${internalId}`,
      options,
    );
  }

  createEmployeeInvitation(
    data: TCreateInvitationForm,
    options?: TApiServiceOptions,
  ) {
    return authenticatedApiService.post(
      API.create_employee_invitation,
      data,
      options,
    );
  }

  resendEmployeeInvitation(
    data: TResendInvitationForm,
    options?: TApiServiceOptions,
  ) {
    return authenticatedApiService.post(
      API.resend_employee_invitation,
      data,
      options,
    );
  }

  cancelEmployeeInvitation(internalId: string, options?: TApiServiceOptions) {
    return authenticatedApiService.delete(
      `${API.cancel_employee_invitation}?internal_id=${internalId}`,
      options,
    );
  }

  getInvitationStats(options?: TApiServiceOptions) {
    return authenticatedApiService.get<TInvitationStatsResponse>(
      API.invitation_stats,
      options,
    );
  }

  // Public invitation endpoints (no authentication required)
  validateInvitationToken(token: string, options?: TApiServiceOptions) {
    return authenticatedApiService.get<TValidateInvitationTokenResponse>(
      `${API.validate_invitation_token}?token=${token}`,
      options,
    );
  }

  acceptInvitation(data: TAcceptInvitationForm, options?: TApiServiceOptions) {
    return authenticatedApiService.post(API.accept_invitation, data, options);
  }

  // Tenant settings operations
  getTenantSettings(options?: TApiServiceOptions) {
    return authenticatedApiService.get<TTenantSettingsResponse>(
      API.tenant_employee_settings,
      options,
    );
  }

  updateTenantSettings(
    data: TUpdateTenantSettingsForm,
    options?: TApiServiceOptions,
  ) {
    return authenticatedApiService.put(
      API.update_tenant_employee_settings,
      data,
      options,
    );
  }

  // Export operations
  exportEmployees(query: string, options?: TApiServiceOptions) {
    return authenticatedApiService.get(API.export_employees + query, options);
  }

  // Employee attributes operations
  getEmployeeAttributes(employeeId: number, options?: TApiServiceOptions) {
    return authenticatedApiService.get(
      `${API.employees}${employeeId}/attributes/`,
      options,
    );
  }

  setEmployeeAttributes(
    employeeId: number,
    data: { attributes: Record<string, any>; change_reason?: string },
    options?: TApiServiceOptions,
  ) {
    return authenticatedApiService.put(
      `${API.employees}${employeeId}/attributes/`,
      data,
      options,
    );
  }

  getEmployeeAttributeHistory(
    employeeId: number,
    options?: TApiServiceOptions,
  ) {
    return authenticatedApiService.get<TEmployeeAttributeHistoryResponse>(
      `${API.employees}${employeeId}/attributes/history/`,
      options,
    );
  }

  getEmployeeAvailablePermissions(
    employeeId: string,
    query?: string,
    options?: TApiServiceOptions,
  ) {
    return authenticatedApiService.get<
      TApiResponseWrapper<TAssignedPermissionListResponse>
    >(
      `${API.employee_available_permissions}${employeeId}/${query ?? ""}`,
      options,
    );
  }

  getEmployeeAssignedPermissions(
    employeeId: string,
    query: string,
    options?: TApiServiceOptions,
  ) {
    return authenticatedApiService.get<
      TApiResponseWrapper<TAssignedPermissionListResponse>
    >(`${API.employee_assigned_permissions}${employeeId}/${query}`, options);
  }

  assignPermissionsToEmployee(
    data: TBulkEmployeePermissionAssignmentRequest,
    options?: TApiServiceOptions,
  ) {
    return authenticatedApiService.post<
      TApiResponseWrapper<TBulkEmployeePermissionAssignmentResponse>
    >(`${API.assign_permissions_to_employee}`, data, options);
  }
}

export const employeeManagementService = new EmployeeManagementService();
